/* eslint-disable react/sort-comp */
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import MuiDialog from '@mui/material/Dialog';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import AllMappingDimensions from 'relevant-shared/mappingDimensions/allMappingDimension';
import { capitalize, percentChange } from 'relevant-shared/misc/misc';
import Constants from 'relevant-shared/reportData/constants';
import AllCustomizers from 'relevant-shared/reportData/customizers/allCustomizers';
import * as DynamicDateRanges from 'relevant-shared/reportData/dynamicDateRanges';
import Granularity from 'relevant-shared/reportData/granularity';
import metricSettings from 'relevant-shared/reportData/metricSettings';
import ReportCalculator from 'relevant-shared/reportData/reportCalculator';
import ReportData from 'relevant-shared/reportData/reportData';
import { getValidStartEnd } from 'relevant-shared/reportData/utils';
import { updateWhereIn } from 'relevant-shared/misc/userToObjectVisibility';
import classes from '../../api/classes';
import Base from '../../layouts/Base';
import BrowserUtils from '../../lib/browserUtils';
import DateUtils from '../../lib/dateUtils';
import MiscUtils from '../../lib/miscUtils';
import { fmtNum } from '../../lib/numberUtils';
import SystemData from '../../lib/systemData';
import { stores } from '../../stores';
import { ConfirmDialog } from '../ConfirmDialog';
import { Dialog } from '../Dialog';
import Disabler from '../Disabler';
import OperationWrapper from '../OperationWrapper';
import Select from '../Select';
import TextField from '../TextField';
import TimezoneSelect from '../TimezoneSelect';
import ChartSettings from './ChartSettings';
import ChartsWrapper from './chartsWrapper';
import TrendIcon from './TrendIcon';
import { DynamicDateRangeSelector } from './dynamicDateRangeSelector';
import ChartNotSupportedAlert from './ChartNotSupportedAlert';
import ReportAsUrl from './reportAsUrl';
import reportBlockingReason from './reportBlockingReason';
import ReportSettings from './reportSettings';
import ReportTable from './reportTable';
import { dbDecodedTableState, dbEncodedTableState } from './reportTableUtils';
import styles from './styles.css';
import { CompareToSwitch,
	ForecastToggle,
	ReportTypeSelector,
	StartEndPicker,
	getUserGroupByOptions,
	isForecastAvailable } from './utils';
import { useReportStore } from '../../stores/ReportStore';
import { Tooltip } from '../Tooltip';

const { Event } = classes;

class Report extends React.Component {
	constructor(props) {
		super(props);
		Object.assign(this, Constants[props.type]);
		this.state = { groupByOptions: this.GROUP_BY_OPTIONS, columns: props.chartSettings?.maxChartsPerRow || 1 };
		Object.assign(this.state, {
			...this.getInitState(props),
			...getValidStartEnd(props, new Date()),
			events: null,
		});
		if (!this.state.sums) {
			this.state.sums = this.getInitSums();
		}
		if (this.state.chartSettings?.chartType === 'default') {
			this.state.chartSettings.chartType = 'auto'; // Fix for some old saved templates/dashboards
		}
		// Date-format etc. might have changed. This clears all caches.
		this.onGenericDataUpdated = () => this.updateState(_.pick(this.state, 'reportData'));
	}

	chartWrapperCalculator(params = {}) {
		const { calculator } = this.state;
		// eslint-disable-next-line no-multi-assign
		const cache = (this.__chartCalculatorCache = this.__chartCalculatorCache || []);
		_.remove(cache, (entry) => entry.calculator !== calculator);
		const chartCategory = ['pie', 'geo'].find((ty) => params[ty]) || 'normal';
		let res = _.find(cache, { chartCategory })?.res;
		if (!res) {
			res = calculator.rearrangedForChartWrapper(chartCategory);
			cache.push({ calculator, chartCategory, res });
		}
		return res;
	}

	setCurrentReportSettings(reset) {
		const { isActualReport } = this.props;
		if (isActualReport) {
			useReportStore.getState().setCurrentReportSettings(reset ? null : this.getCurrentProperties());
		}
	}

	componentDidMount() {
		this.setCurrentReportSettings();
		this.updateDimensions();
		SystemData.onGenericDataUpdated.add(this.onGenericDataUpdated);
		window.addEventListener('resize', this.updateDimensions);
	}

	shouldComponentUpdate(nextProps, nextState) {
		const stateHasChanged = !_.isEqual(
			_.omit(this.state, 'tableState'),
			_.omit(nextState, 'tableState'),
		);
		const propsHaveChanged = !_.isEqual(this.props, nextProps);
		return stateHasChanged || propsHaveChanged;
	}

	componentWillUnmount() {
		this.setCurrentReportSettings(true);
		SystemData.onGenericDataUpdated.remove(this.onGenericDataUpdated);
		window.removeEventListener('resize', this.updateDimensions);
	}

	getInitState(props) {
		const state = _.pick(props, Object.keys(Report.propTypes));
		// May be undefined for Xandr log reports
		if (!SystemData.genericData.userReportOptions[props.type]) {
			return state;
		}
		const { USER_SUM_OPTIONS } = SystemData.genericData.userReportOptions[props.type];
		/* The database may sometimes (sadly) be in a bad state, so check if sums/groupBy contains dimensions that
		/ are not actually allowed, i.e is not included in USER_SUM_OPTIONS/USER_GROUP_BY_OPTIONS, and filter those out.
		*/
		if (!stores.identity.isAdministrator()) {
			if (state.sums) {
				state.sums = state.sums.filter((dimension) => USER_SUM_OPTIONS.includes(dimension));
				if (state.sums.length === 0) {
					state.sums = [USER_SUM_OPTIONS[0]];
				}
			}
			const extraAllow = (dim) => this.getCustomizer().allowAdditionalUserGroupByOption(dim);
			// If we've filtered out everything/is undefined, set to first available option.
			const userGroupByOptions = this.getUserGroupByOptions();
			if (state.groupBy) {
				state.groupBy = state.groupBy.filter((dimension) => (
					userGroupByOptions.includes(dimension) || extraAllow(dimension)
				));
				// If we've filtered out everything/is undefined, set to first available option.
				if (state.groupBy.length === 0) {
					state.groupBy = [userGroupByOptions[0]];
				}
			}

			if (state.whereIn) {
				const keys = Object.keys(state.whereIn);
				keys.forEach((key) => {
					// Special case: normal users's shouldn't be allowed to group by user (sales rep.)
					// But it should still be possible to filter by own advertisers (userNr: [my-id])
					if (!userGroupByOptions.includes(key) && !extraAllow(key) && key !== 'userNr') {
						delete state.whereIn[key];
					}
				});
			}
		}
		// Handle old saved reports since the time userNr was saved as numbers as strings
		if (state.whereIn?.userNr?.length) {
			state.whereIn.userNr = state.whereIn.userNr.map(Number);
		}
		return state;
	}

	updateDimensions = (props) => {
		const { maxChartsPerRow } = props || {};
		const width = this.node?.clientWidth || 1;
		const { chartSettings } = this.state;
		const columns = Constants.MAX_COLUMNS(maxChartsPerRow || chartSettings?.maxChartsPerRow || 1, width);
		if (this.state.columns !== columns) {
			this.updateState({ columns });
		}
	};

	getCustomizer() {
		const { reportData } = this.state;
		return reportData ? reportData.customizer : AllCustomizers.createByType(this.props.type, null);
	}

	getInitSums() {
		return this.getCustomizer().getInitSums({ isAdministrator: stores.identity.isAdministrator() });
	}

	static getCurrentPropertiesFromState(state, userAdjusted) {
		const res = _.omit(_.clone(_.pickBy(state, (v, k) => Report.propTypes[k])), [
			'settingsOpen', 'trigger', 'header', 'chartSettingsOpen', 'isActualReport', 'warnOldData',
		]);
		res.tableState = dbEncodedTableState(res.tableState);
		if (userAdjusted) {
			res.whereIn = _.cloneDeep(res.whereIn || {});
			updateWhereIn(res.whereIn, stores.identity);
		}
		return res;
	}

	getCurrentProperties(userAdjusted) {
		return Report.getCurrentPropertiesFromState(this.state, userAdjusted);
	}

	getGroupByOption() {
		return this.state.groupByOptions || this.GROUP_BY_OPTIONS;
	}

	getGranularityOptions() {
		return _.pick(
			Granularity.getOptions(),
			this.state.reportData.customizer.supportedGranularities(),
		);
	}

	getServerSettings() {
		return this.state.reportData.settings;
	}

	getCalculator() {
		return this.state.calculator;
	}

	updateState(newState) {
		const { onSettingsChanged } = this.props;
		const newFullState = { ...this.state, ...newState };
		let extra;
		if (newFullState.reportData) {
			extra = { calculator: new ReportCalculator(newFullState) };
		}
		const NON_SIGNIFICANT_FOR_CHANGED = ['tableState']; // Don't call onSettingsChanged just for these updates
		const getProps = () => _.omit(this.getCurrentProperties(), NON_SIGNIFICANT_FOR_CHANGED);
		const prevProps = getProps();
		this.setState({
			...newState,
			...extra,
		}, () => {
			if (!_.isEqual(prevProps, getProps())) {
				this.setCurrentReportSettings();
				onSettingsChanged?.(this.state);
			}
		});
	}

	async update(state, reloadReport, directState) {
		BrowserUtils.keepScrollPos(() => this.updateInternal(state, reloadReport, directState));
	}

	async updateInternal(state, reloadReport, directState) {
		const fullState = { ...state, ...directState };
		if (!reloadReport) {
			this.updateState(fullState);
			return;
		}
		this.updateState({ reloading: true, ...directState });
		try {
			await this.requestReport({ ...fullState, reloading: false });
		} catch (e) {
			Base.renderGlobal((done) => (
				<Dialog
					open
					status="error"
					text={MiscUtils.errorMsg(e)}
					onClose={() => {
						this.updateState({ reloading: false });
						done();
					}}
				/>
			));
		}
	}

	async requestReport(withState) {
		const reportState = { ...this.state, ...withState };
		const startEnd = getValidStartEnd(reportState);
		const reportData = await ReportData.from({ ...reportState, ...startEnd });
		setTimeout(() => this.updateState({
			...withState,
			...startEnd,
			calcSums: reportData.calcSumsMap,
			serverSums: reportData.allDataSums(),
			reportData,
			groupByOptions: reportData.customizer.getGroupByOptions(this, reportData.report, reportData.settings),
		}));
	}

	calculateSumVal(dataObj, sumVal, fallbackIfInvalid, useComp) {
		const { reportData } = this.state;
		if (fallbackIfInvalid !== undefined) {
			const invalidFn = Constants[this.props.type].IS_INVALID_AS_TOTAL;
			if (invalidFn && invalidFn(this.state, sumVal)) {
				return fallbackIfInvalid;
			}
		}
		return reportData.calculate(dataObj, sumVal, useComp);
	}

	renderGranularityPicker() {
		const { granularity } = this.state;
		const options = this.getGranularityOptions();
		const needReload = (newGranularity) => {
			const serverGranularity = this.getServerSettings().granularity;
			const newTimeRange = Constants.SHORT_TIME_RANGES[newGranularity];
			const serverTimeRange = Constants.SHORT_TIME_RANGES[serverGranularity];
			return newTimeRange && (!serverTimeRange || serverTimeRange.ms > newTimeRange.ms);
		};
		return (
			<Box sx={{ ml: 1 }}>
				<Select
					name="Show"
					label="Show"
					items={_.map(options, (v, k) => ({ label: v, value: k }))}
					value={granularity}
					onChange={(ev) => this.update({ granularity: ev.target.value }, needReload(ev.target.value))}
					underlineShow={false}
				/>
			</Box>
		);
	}

	limitSumsByGrouping(initSums, groupBy) {
		let sums = initSums;
		const limiters = this.GROUP_BY_SUM_LIMITERS || {};
		groupBy.forEach((by) => {
			const limiter = limiters[by];
			if (limiter) {
				sums = sums.filter((sum) => limiter.indexOf(sum) >= 0);
			}
		});
		return sums;
	}

	updateGroupBy(groupBy, stateSums) {
		let sums = this.limitSumsByGrouping(stateSums, groupBy);
		if (!sums.length) {
			sums = this.getInitSums();
		}
		return sums;
	}

	getUserGroupByOptions() {
		const { type } = this.props;
		const res = getUserGroupByOptions(type);
		res.push(...this.getCustomizer().getAdditionalUserGroupByOptions(this.state));
		return _.uniq(res);
	}

	getDimensionExclude(state) {
		const { groupByOptions } = this.state;
		return _.filter([
			state.useForecast && this.NON_FORECAST_OPTIONS,
			this.getCustomizer().getDimensionExclude(state, groupByOptions),
		]).flat();
	}

	availableOptionsFor(idx, stateObj) {
		const state = stateObj || this.state;
		const { groupByOptions } = this.state;
		const { groupBy } = state;
		const isAdmin = stores.identity.isAdministrator();
		const userGroupByOptions = this.getUserGroupByOptions();
		const excludeDims = this.getDimensionExclude(state);
		return _.pickBy(groupByOptions, (val, key) => {
			if (excludeDims.indexOf(key) >= 0) {
				return false;
			}
			if (idx !== undefined && groupBy[idx] === key) { // selected option should always be available
				return true;
			} if (groupBy.indexOf(key) >= 0) { // options selected elsewhere should *not* be available
				return false;
			}
			if (!isAdmin) {
				if (userGroupByOptions.indexOf(key) < 0) {
					return false;
				}
			}
			return true;
		});
	}

	exportAsCsv = () => {
		const { calculator, title } = this.state;
		return BrowserUtils.downloadTextFile(calculator.reportToCsv(), `${title} ${new Date()}.csv`);
	};

	renderIconButtons() {
		const {
			onSave,
			onRemove,
			settingsOpen,
			onSettingsOpenToggle,
			isActualReport,
			onTitleChange,
		} = this.props;
		const { reportData } = this.state;
		return (
			<>
				{!isActualReport && (
					<>
						<ReportAsUrl
							getUserAdjustedProperties={() => _.omit(this.getCurrentProperties(true), 'isActualReport')}
						/>
						<Tooltip title="Export as .CSV">
							<IconButton
								onClick={() => this.exportAsCsv()}
								disableTouchRipple
								size="large"
							>
								<FileDownloadIcon />
							</IconButton>
						</Tooltip>
						{!!onSettingsOpenToggle && (
							<Tooltip title="Open settings">
								<IconButton
									onClick={() => onSettingsOpenToggle(true)}
									disableTouchRipple
									size="large"
								>
									<SettingsIcon />
								</IconButton>
							</Tooltip>
						)}
					</>
				)}
				{onSave
				&& (
					<Tooltip title="Save widget">
						<IconButton onClick={() => onSave()} disableTouchRipple size="large">
							<SaveIcon />
						</IconButton>
					</Tooltip>
				)}
				{onRemove
				&& (
					<Tooltip title="Remove widget">
						<div>
							<ConfirmDialog
								text="Are you sure you want to delete this report widget?"
								onConfirm={() => onRemove()}
							>
								<IconButton><DeleteIcon /></IconButton>
							</ConfirmDialog>
						</div>
					</Tooltip>
				)}
				<MuiDialog
					maxWidth="sm"
					fullWidth
					open={!!settingsOpen}
					onClose={() => onSettingsOpenToggle(false)}
				>
					{settingsOpen
					&& (
						<ReportSettings
							report={this}
							reportData={reportData}
							settings={_.clone(this.state)}
							settingsFilter={Object.keys(Report.propTypes)}
							initSums={this.getInitSums()}
							advMappings={(reportData.report || {}).advMappingData || []}
							groupByOptions={this.getGroupByOption()}
							onSave={(settings) => {
								this.update(settings, true);
								onSettingsOpenToggle(false);
								onTitleChange(settings.title);
							}}
							onCancel={() => onSettingsOpenToggle(false)}
							funcs={{
								limitSumsByGrouping: this.limitSumsByGrouping.bind(this),
								updateGroupBy: this.updateGroupBy.bind(this),
								getUserGroupByOptions: this.getUserGroupByOptions.bind(this),
								availableOptionsFor: this.availableOptionsFor.bind(this),
								getDimensionExclude: this.getDimensionExclude.bind(this),
							}}
							useForecast={this.state.useForecast}
						/>
					)}
				</MuiDialog>
			</>
		);
	}

	renderPieErrorMessage() {
		const { groupBy, useCompareTo } = this.state;

		const byDate = groupBy.length === 1 && groupBy[0] === 'date';

		if (byDate) {
			return (
				<ChartNotSupportedAlert
					onFix={() => this.update({ showPieChart: false })}
					suggestion="Turn off pie chart"
				>
					The pie chart visualisation is not supported
					for the &quot;date&quot; dimension
				</ChartNotSupportedAlert>
			);
		}
		if (useCompareTo) {
			return (
				<ChartNotSupportedAlert
					onFix={() => this.update({ useCompareTo: false })}
					suggestion="Remove comparison"
				>
					The pie chart visualisation is not supported
					when a comparison date range is selected
				</ChartNotSupportedAlert>
			);
		}
		return null;
	}

	renderGeoChartErrorMessage() {
		const { groupBy } = this.state;

		if (!groupBy.includes('country')) {
			return (
				<ChartNotSupportedAlert
					onFix={() => this.update({ showGeoChart: false })}
					suggestion="Turn off geo chart"
				>
					The geo chart visualisation is not supported
					without the &quot;country&quot; dimension
				</ChartNotSupportedAlert>
			);
		}

		return null;
	}

	getTrendInformation({
		label = '', key, current, historical, decimals = 2,
	}) {
		if (current === historical || historical === undefined) {
			return { label };
		}

		const { sums } = this.state;
		const { type } = this.props;

		const settings = metricSettings[type] || {};
		const { isNegativeIfIncrease, isPercentage } = settings[key || sums[0]] || {};

		const caluclateDifference = () => {
			const rounded = (val) => +parseFloat(val).toFixed(decimals);
			if (isPercentage) {
				return rounded(current - historical);
			}
			return rounded(percentChange(historical /* from */, current /* to */));
		};

		const diff = caluclateDifference();
		if (diff === 0) {
			return { label };
		}

		const isPositive = (neg) => (neg ? diff < 0 : diff > 0);
		const color = isPositive(isNegativeIfIncrease) ? 'green' : 'red';
		const diffDisplay = diff < 1000 ? diff.toFixed(2) : diff;

		return {
			label,
			color,
			sign: isPositive() ? '+' : '',
			diff,
			isPercentage,
			isPositive: isPositive(),
			formatted: `${isPositive() ? '+' : ''}${diffDisplay}${isPercentage ? '%pt.' : '%'}`,
		};
	}

	renderTopBar() {
		const { timezone } = this.state;
		const customizer = this.getCustomizer();
		return (
			<Grid container spacing={3} sx={{ mb: -2, mt: -6 }}>
				{customizer.hasTimezoneSupport() && (
					<Grid item style={{ minWidth: 200 }}>
						<TimezoneSelect
							value={timezone}
							onChange={(ev) => this.update({ timezone: ev.target.value }, true)}
						/>
					</Grid>
				)}
				<Grid item sx={{ flexGrow: 1 }}>
					{customizer.renderTopBar?.(this.state, (state) => this.update(state, true))}
				</Grid>
			</Grid>
		);
	}

	renderInternal() {
		const { cardProps } = this.props;

		const {
			reportData,
			groupBy,
			title,
			sums,
			showChart,
			showTable,
			showPieChart,
			showGeoChart,
			hideSettings,
			serverSums,
			sortSum,
			sortSumIsComp,
			sortReversed,
			rowsPerTab,
			calculator,
			importanceTabPerc,
			trigger,
			chartSettings,
			chartSettingsOpen,
			useCompareTo,
			events,
		} = this.state;

		const { isActualReport } = this.props;
		const isAdmin = stores.identity.isAdministrator();
		const hasDate = groupBy.includes('date');
		const displayChartSettings = (showChart || showPieChart) && chartSettingsOpen;
		const displayMergeDimensionSettings = calculator?.settings?.groupBy?.filter((dim) => dim !== 'date').length > 1;
		const groupByOptions = this.getGroupByOption();
		const { report, customizer } = reportData;
		const warningMsg = customizer.getReportWarning?.({ reportData, calculator });
		const blocking = reportBlockingReason({
			reportData,
			onUpdateState: (...args) => this.update(...args),
		});

		const currencyOf = (sum) => {
			const description = this.SUM_DESCRIPTIONS[sum];
			const currency = this.IS_REVENUE_SUM[sum]
				? ` ${report.currency}`
				: '';
			return `${description}${currency}`;
		};
		function descriptionOf(sum) {
			return this.SUM_DESCRIPTIONS[sum];
		}

		const formattedData = calculator.formatReport(groupBy.length, true);

		const summary = calculator.summarizeNumbers(report.data);
		const [data, dataComp] = [false, ...(useCompareTo ? [true] : [])].map((useComp) => {
			const val = this.calculateSumVal(summary, sums[0], null, useComp);
			return val === null ? null : {
				val,
				formatted: `${fmtNum(val)} ${this.IS_REVENUE_SUM[sums[0]] ? report.currency : ''}`,
			};
		});

		const [dataSum, dataSumComp] = [data?.formatted, dataComp?.formatted];

		const formattedLabel = (this.SUM_DESCRIPTIONS[sums[0]] || sums[0])
			.toLowerCase()
			.replace('total ', '');

		const canShowEvents = isAdmin && showChart && hasDate;
		const loadEventsIfNeeded = async () => {
			const { events: existingEvents } = this.state;
			if (chartSettings?.showEventLines && canShowEvents && !existingEvents) {
				this.setState({ events: await Event.fnCache.getEvents() });
			}
		};
		loadEventsIfNeeded();

		const renderChartSettings = (extraProps) => {
			if (!showChart && !showPieChart && !showGeoChart) {
				return null;
			}
			const onChartSettingsClick = () => {
				this.updateState({
					chartSettingsOpen: !chartSettingsOpen,
				});
			};

			return (
				<ChartSettings
					autoChartType={calculator.properties().chartType}
					showChart={showChart}
					showPieChart={showPieChart}
					showGeoChart={showGeoChart}
					chartSettingsOpen={chartSettingsOpen ?? false}
					onChartSettingsClick={onChartSettingsClick}
					onShowEventLinesChange={() => {
						const updatedChartSettings = {
							...chartSettings,
							showEventLines: !chartSettings.showEventLines,
						};
						this.updateState({
							...this.state,
							chartSettings: updatedChartSettings,
						});
					}}
					onChartSettingsChange={(updatedFields) => {
						this.updateState({
							chartSettings: {
								...chartSettings,
								...updatedFields,
							},
						});
						this.updateDimensions(updatedFields);
					}}
					onEventsChange={(updatedEvents) => {
						this.setState({ events: updatedEvents });
					}}
					chartSettings={chartSettings}
					canShowEvents={canShowEvents}
					displayMergeDimensionSettings={displayMergeDimensionSettings}
					groupBy={groupBy}
					groupByOptions={groupByOptions}
					eventTags={_.uniq((events || []).map(({ tags }) => tags).flat())}
					onClose={onChartSettingsClick}
					{...extraProps}
				/>
			);
		};

		const { columns } = this.state;

		const renderChart = (extraProps = {}) => (
			<ChartsWrapper
				columns={columns}
				events={canShowEvents ? events : null}
				calculator={this.chartWrapperCalculator(extraProps)}
				referenceData={formattedData.arr}
				sums={sums}
				currencyOf={currencyOf}
				descriptionOf={descriptionOf.bind(this)}
				chartSettings={chartSettings}
				hasDateDimension={groupBy.includes('date')}
				displayChartSettings={displayChartSettings}
				displayMergeDimensionSettings={displayMergeDimensionSettings}
				groupByOptions={groupByOptions}
				dimensions={groupBy
					.filter((dim) => dim !== 'date')
					.map((dim) => ({ label: groupByOptions[dim], value: dim }))}
				{...extraProps}
			/>
		);

		const renderSum = (sumStr, label) => (
			<TextField
				name={Math.random().toString()}
				label={label}
				value={sumStr}
				underlineShow={false}
				nonEditable
				className={styles.totalInput}
				inputProps={{ size: 1 }}
			/>
		);

		const renderSumBoxes = () => {
			const box = (r, children) => (
				<Box sx={{ mt: 2 }}>
					{r}
					{children}
				</Box>
			);
			const mainLabel = capitalize(formattedLabel);
			const mainKey = sums[0];
			const comparisonLabel = `Historical ${formattedLabel.toLowerCase()}`;
			const trendIcon = TrendIcon(this.getTrendInformation({
				label: mainLabel,
				key: mainKey,
				current: data.val,
				historical: dataComp?.val,
			}));
			return box(
				renderSum(dataSum, trendIcon),
				dataSumComp ? box(renderSum(dataSumComp, comparisonLabel)) : null,
			);
		};

		return (
			<Card
				variant="outlined"
				sx={{ overflow: 'visible', userSelect: 'text' }}
				{...cardProps}
			>
				<CardContent sx={{ mt: 0 }}>
					<Box display="flex" alignItems="center" marginBottom={hideSettings ? 0 : 4}>
						{!isActualReport && (
							<Typography variant="h2" className={styles.title}>
								{title}
							</Typography>
						)}
						{this.renderIconButtons()}
					</Box>
					{!hideSettings
					&& (
						<>
							{this.renderTopBar()}
							<Box
								sx={{
									display: 'flex',
									flexWrap: 'nowrap',
									justifyContent: 'space-between',
								}}
							>
								<Box
									sx={{ display: 'flex', flexWrap: 'wrap' }}
								>
									<Box
										display="flex"
										sx={{
											maxHeight: (theme) => theme.spacing(7),
											mt: 3,
										}}
									>
										<ReportTypeSelector
											state={this.state}
											update={(s) => this.update(s)}
										/>
									</Box>
									<Box
										sx={{
											display: 'flex',
											mt: 3,
											ml: 1,
										}}
									>
										<StartEndPicker
											report={this}
											state={this.state}
											isActive={!this.state.dynamicDateRange}
											update={(settings) => this.update(settings, true)}
											renderAsForecasted={(d) => reportData.report.firstForecastDate && DateUtils.toDate(d) >= DateUtils.fullDay(reportData.report.firstForecastDate)}
											useSpaceBetween
										/>
									</Box>
									<Box sx={{ pt: 2, ml: 1, mt: 1 }}>
										<DynamicDateRangeSelector
											update={(settings) => this.update(settings, true)}
											currentDynamicDateRange={this.state.dynamicDateRange}
											dateRanges={customizer.getDynamicDateRangesValues()}
											supportedGranularities={customizer.supportedGranularities()}
											allowToday={customizer.hasDataForToday()}
										/>
									</Box>
									<Box sx={{ mt: 3, mr: 1 }}>
										{hasDate && this.renderGranularityPicker()}
									</Box>
									<Box sx={{ mt: 3 }}>
										<FormGroup>
											{isForecastAvailable(isAdmin) && (
												<ForecastToggle
													state={this.state}
													customizer={customizer}
													update={(s) => this.update(s, true)}
												/>
											)}
											<CompareToSwitch
												state={this.state}
												update={(settings) => this.update(settings, true)}
											/>
										</FormGroup>
									</Box>
								</Box>
								{dataSum !== undefined && renderSumBoxes()}
							</Box>
						</>
					)}
					{renderChartSettings()}
					{!blocking && showPieChart && (
						<Box marginTop={2}>
							{this.renderPieErrorMessage() || renderChart({
								onDisableChartType: () => this.update({ showPieChart: false }),
								pie: true,
								chartSettings: {
									...chartSettings,
								},
							})}
						</Box>
					)}
					{warningMsg && (
						<Box marginTop={2}>
							{warningMsg}
						</Box>
					)}
					{!blocking && showGeoChart && (
						<Box marginTop={2}>
							{this.renderGeoChartErrorMessage() || renderChart({ geo: true })}
						</Box>
					)}
					{!blocking && showChart && renderChart()}
					{!blocking && showTable && (
						<ReportTable
							{...this.getCurrentProperties()}
							calculator={calculator}
							onExport={() => this.exportAsCsv()}
							calculateSumVal={(...args) => this.calculateSumVal(...args)}
							getTrendInformation={(...args) => this.getTrendInformation(...args)}
							data={formattedData}
							serverSums={serverSums}
							sortReversed={sortReversed}
							sortSum={sortSum}
							sortSumIsComp={sortSumIsComp}
							onSettingsChanged={async (settings, reload = false) => this.update(settings, reload)}
							rowsPerTab={rowsPerTab || Report.defaultProps.rowsPerTab}
							importanceTabPerc={importanceTabPerc || 0}
							groupByOptions={this.getGroupByOption()}
							tableState={dbDecodedTableState(this.props.tableState)}
							reportData={reportData}
							trigger={trigger}
							funcs={{
								limitSumsByGrouping: this.limitSumsByGrouping.bind(this),
								availableOptionsFor: this.availableOptionsFor.bind(this),
								getInitSums: this.getInitSums.bind(this),
							}}
						/>
					)}
					{blocking}
				</CardContent>
			</Card>
		);
	}

	render() {
		const { header } = this.props;
		const { reloading, reportData } = this.state;
		return (
			<Grid container spacing={0} sx={{ zIndex: 0 }}>
				<Grid item xs={12}>
					<div ref={(node) => { this.node = node; }}>
						{ header }
						<Disabler disabled={!!reloading}>
							<OperationWrapper
								fn={() => this.requestReport()}
							>
								{reportData && this.renderInternal()}
							</OperationWrapper>
						</Disabler>
					</div>
				</Grid>
			</Grid>
		);
	}
}

Report.propTypes = {
	title: PropTypes.string,
	start: PropTypes.any,
	end: PropTypes.any,
	timezone: PropTypes.any,
	tableState: PropTypes.objectOf(PropTypes.any),
	dynamicDateRange: PropTypes.oneOf(DynamicDateRanges.getAllDbKeys()),
	compareTo: PropTypes.any,
	useCompareTo: PropTypes.bool,
	granularity: PropTypes.oneOf(['perday', 'perweek', 'permonth', 'peryear', 'perhour', 'per10min']),
	groupBy: PropTypes.array,
	whereIn: PropTypes.object,
	sums: PropTypes.array,
	showChart: PropTypes.bool,
	showTable: PropTypes.bool,
	showPieChart: PropTypes.bool,
	showGeoChart: PropTypes.bool,
	settingsOpen: PropTypes.bool,
	hideSettings: PropTypes.bool,
	mergePublicHidden: PropTypes.bool,
	splitByAuctionRunner: PropTypes.bool,
	maxAdvertisers: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number,
	]),
	includeNoUserAdvertisers: PropTypes.bool,
	..._.transform(AllMappingDimensions, (res, { reportProp }) => {
		res[reportProp] = PropTypes.string;
	}, {}),
	currency: PropTypes.string,
	type: PropTypes.string,
	sortSum: PropTypes.string,
	sortSumIsComp: PropTypes.bool,
	sortReversed: PropTypes.bool,
	rowsPerTab: PropTypes.number,
	importanceTabPerc: PropTypes.number,
	trendMethod: PropTypes.string,
	trendPeriods: PropTypes.number,
	useForecast: PropTypes.bool,
	correctionType: PropTypes.string,
	filterFlags: PropTypes.object,
	attributes: PropTypes.object,
	trigger: PropTypes.object, // Only when editing triggers
	triggerFilters: PropTypes.arrayOf(
		PropTypes.shape({
			sum: String,
			isComp: Boolean,
			cond: PropTypes.oneOf(['lt', 'gtEq']),
			value: Number,
		}),
	),
	chartSettings: PropTypes.shape({
		fill: PropTypes.bool,
		stacked: PropTypes.bool,
		barChartType: PropTypes.oneOf(['horizontal', 'vertical']),
		chartType: PropTypes.oneOf(['auto', 'line', 'bar', 'pie']),
		showEventLines: PropTypes.bool,
		selectedEventTags: PropTypes.arrayOf(PropTypes.string),
		maxChartsPerRow: PropTypes.number,
	}),
	header: PropTypes.element,
	isActualReport: PropTypes.bool,
	onTitleChange: PropTypes.func,
	onSettingsChanged: PropTypes.func,
	filterSelectionType: PropTypes.objectOf(PropTypes.oneOf(['INCLUSIVE', 'EXCLUSIVE'])),
	warnOldData: PropTypes.bool,
	filterUniqueDimIdx: PropTypes.number, // idx to groupBy
	ignoreUserSettings: PropTypes.bool,
};

Report.defaultProps = {
	title: 'My report',
	start: -31,
	end: -1,
	tableState: undefined,
	compareTo: undefined,
	dynamicDateRange: undefined,
	useCompareTo: false,
	granularity: 'perday',
	groupBy: ['date'],
	whereIn: undefined,
	sums: undefined,
	showChart: true,
	showTable: false,
	showPieChart: false,
	showGeoChart: false,
	settingsOpen: false,
	hideSettings: false,
	mergePublicHidden: false,
	splitByAuctionRunner: false,
	maxAdvertisers: 200,
	includeNoUserAdvertisers: false,
	..._.transform(AllMappingDimensions, (res, { reportProp }) => {
		res[reportProp] = undefined;
	}, {}),
	currency: undefined,
	timezone: undefined,
	type: 'audience',
	sortSum: undefined,
	sortSumIsComp: false,
	sortReversed: false,
	rowsPerTab: 100,
	importanceTabPerc: 0, // 100%
	trendMethod: 'median',
	trendPeriods: 3,
	useForecast: false,
	correctionType: undefined,
	filterFlags: {},
	attributes: undefined,
	trigger: undefined,
	triggerFilters: undefined,
	chartSettings: {
		fill: false,
		stacked: false,
		groupedDimensions: [],
		barChartType: 'vertical',
		showEventLines: false,
		selectedEventTags: [],
		maxChartsPerRow: 1,
		chartType: 'auto',
		geoOutlierThreshold: 0.05,
		geoDontColorOutliers: false,
	},
	header: null,
	// If its rendered from reports, not dashboards or alarms.
	isActualReport: false,
	onTitleChange: () => undefined,
	onSettingsChanged: () => undefined,
	filterSelectionType: {
		publisherId: 'INCLUSIVE',
		placementId: 'INCLUSIVE',
		siteId: 'INCLUSIVE',
		sourceId: 'INCLUSIVE',
		sspId: 'INCLUSIVE',
		mediaTypeId: 'INCLUSIVE',
		paymentTypeId: 'INCLUSIVE',
		dealTypeId: 'INCLUSIVE',
		revenueType: 'INCLUSIVE',
		demandChannel: 'INCLUSIVE',
		..._.transform(AllMappingDimensions, (res, { reportProp }) => {
			res[reportProp] = 'INCLUSIVE';
		}, {}),
	},
	warnOldData: false,
	filterUniqueDimIdx: 0,
	ignoreUserSettings: false,
};

export default Report;
